import React, { useEffect, useState } from "react";
import { PhoneIcon, QuestionAnswerIcon } from "@origin-digital/icon";
import { logger } from "@origin-digital/reporting-client";
import { isExperienceEnabled } from "@origin-digital/features";
import content from "../../cms/chat.json";
import { useAppContext } from "../../AppContext";
import { checkLiveChatAvailability } from "../../helpers/livechat.helper";
import { getLiveChat, addNoTranslateClass } from "../../services/livechat";
import { Chat } from "./Chat";
import { MessengerIcon } from "./MessengerIcon";
import { StatusType } from "./LiveChatItem";
import { config as appConfig } from "../../config";

const ICON_SIZE = "20";
const POLLING_INTERVAL = 60000;
const LOADING_TIMEOUT = 15000;
const LIVECHAT_TIMEOUT = 15000;
const BOLD_CHAT_ID = "bc-chat-container";

export const bccbId = Math.random().toString();

getLiveChat(
  bccbId,
  appConfig.livechatDepartmentId,
  appConfig.livechatWebsiteId,
  appConfig.livechatWindowId
);

export const ChatController = (): JSX.Element => {
  const { isNative } = useAppContext();

  const boldChat = document.getElementById(bccbId);
  const liveChatLink = boldChat?.getElementsByTagName("a")[0];
  const [status, setStatus] = useState<StatusType>();
  const [chatWindowTimer, setChatWindowTimer] = useState<
    NodeJS.Timeout | undefined
  >();
  const [link, setLink] = useState<HTMLAnchorElement | undefined>(liveChatLink);

  const onLiveChatButtonClick = () => {
    let bcContainer = document.getElementById(BOLD_CHAT_ID);
    if (!bcContainer) {
      setStatus(StatusType.LOADING);
      link?.click();
      const timeout = setTimeout(() => {
        bcContainer = document.getElementById(BOLD_CHAT_ID);
        if (!bcContainer) {
          logger.error("[Live chat] Error: Live chat not loading.");
          setStatus(StatusType.ERROR);
        }
      }, LIVECHAT_TIMEOUT);
      setChatWindowTimer(timeout);
      logger.debug("[Live chat] Starting chat window timer ", timeout);
    }
  };

  // Apple Business Chat POC feature toggled via Adobe Target
  const [, setAppleScriptLoaded] = useState(false);
  useEffect(() => {
    // We don't know when Apple's script has loaded so we'll wait and poll for
    // window.appleBusinessChat for a maximum of 10 seconds
    // This ensures we trigger a re-render if the script has loaded so we can display
    // ABC if it is supported
    const poll = setInterval(() => {
      if (window.appleBusinessChat) {
        clearInterval(poll);
        setAppleScriptLoaded(true);
      }
    }, 100);

    const SECONDS_UNTIL_TIME_OUT = 10 * 1000; // 10 secs
    setTimeout(() => {
      clearInterval(poll);
    }, SECONDS_UNTIL_TIME_OUT);
  }, []);

  let appleBusinessChat;
  try {
    appleBusinessChat =
      isExperienceEnabled("OE-656-mbox") &&
      !!window?.appleBusinessChat?.isSupported();
  } catch (e) {
    logger.error(`Error determining Apple Business Chat support: ${e}`);
  }

  // Disable messenger on mobile native or if Apple Business Chat is available
  const messenger =
    appleBusinessChat || isNative
      ? undefined
      : {
          title: content.messenger.title,
          body: content.messenger.body,
          icon: <MessengerIcon />,
        };

  const other = {
    title: content.others.title,
    body: content.others.body,
    icon: <PhoneIcon size={ICON_SIZE} />,
  };

  const liveChat = {
    title: content.liveChat.title,
    line1: content.liveChat[status?.toLowerCase() || ""]?.line1,
    line2: status === StatusType.WAITING ? "" : content.liveChat.line2,
    icon: <QuestionAnswerIcon size={ICON_SIZE} />,
    status,
    onClick: onLiveChatButtonClick,
    buttonLabel: content.liveChat.button,
  };

  const setLiveChatStatus = (bold360Status?: StatusType) => {
    const inHours = checkLiveChatAvailability();
    logger.debug(`[live-chat] In support hours: ${inHours}`);
    let chatStatus = bold360Status;

    if (!bold360Status) {
      chatStatus = document.getElementById(bccbId)?.textContent as StatusType;
    }

    if (!chatStatus) {
      setStatus(StatusType.WAITING);
    } else if (chatStatus === StatusType.AVAILABLE) {
      setStatus(StatusType.AVAILABLE);
    } else if (chatStatus === StatusType.UNAVAILABLE && inHours) {
      setStatus(StatusType.BUSY);
    } else {
      if (!StatusType[chatStatus!]) {
        logger.error(
          `[live-chat] Live chat status unknown: ${chatStatus}. Defaulting to UNAVAILABLE. In support hours: ${inHours}`
        );
      }
      setStatus(StatusType.UNAVAILABLE);
    }
  };

  useEffect(() => {
    setLiveChatStatus();
  }, []);

  useEffect(() => {
    const config = { childList: true, subtree: true };
    /* istanbul ignore next */
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          const bcLink = boldChat?.getElementsByTagName("a")[0];
          const bcStatus = mutation.addedNodes[0].textContent as StatusType;
          setLink(bcLink);
          setLiveChatStatus(bcStatus);
          // Add no translation tag to anchor link because
          // Parent div (div[id=bccId])'s class gets overridden
          addNoTranslateClass(bcLink);
        }
      });
    });

    if (boldChat) observer.observe(boldChat, config);
    return () => {
      if (observer) observer.disconnect();
    };
  }, []);

  useEffect(() => {
    const config = { childList: true, subtree: false };
    /* istanbul ignore next */
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          if ((mutation.addedNodes[0] as HTMLElement).id === BOLD_CHAT_ID) {
            setLiveChatStatus();
          }
        }

        if (mutation.removedNodes.length) {
          if ((mutation.removedNodes[0] as HTMLElement).id === BOLD_CHAT_ID) {
            if (chatWindowTimer) clearTimeout(chatWindowTimer);
            logger.debug(
              "[Live chat] Clearing chat window timer ",
              chatWindowTimer
            );
            setChatWindowTimer(undefined);
          }
        }
      });
    });

    observer.observe(document.body, config);
    return () => {
      if (observer) observer.disconnect();
    };
  }, [chatWindowTimer]);

  useEffect(() => {
    setTimeout(() => {
      const bcLoaded = document.getElementById(bccbId)?.textContent;
      if (!bcLoaded) {
        logger.error("[live-chat] Bold live chat timeout on initial load.");
        setStatus(StatusType.ERROR);
      }
    }, LOADING_TIMEOUT);
  }, []);

  useEffect(() => {
    let timer: NodeJS.Timeout | undefined;
    if (status !== StatusType.WAITING && status !== StatusType.ERROR) {
      timer = setInterval(() => {
        setLiveChatStatus();
      }, POLLING_INTERVAL);
      logger.debug("[live-chat] Starting timer ", timer);
    }
    return () => {
      logger.debug("[live-chat] Clearing timer ", timer);
      if (timer) {
        clearInterval(timer);
      }
    };
  }, [status]);

  return (
    <Chat
      heading={content.heading}
      liveChat={liveChat}
      messenger={messenger}
      appleBusinessChat={appleBusinessChat}
      other={other}
    />
  );
};
